/**
 * chat.ts — LLM chat for the omnipresent SAGE chat bar.
 *
 * Routes through the same Rust HTTP path as MCP (no browser Origin → no CORS,
 * works in the installed desktop app). Anthropic first, OpenRouter fallback.
 * Keys live in localStorage under the SAME keys the companion uses
 * (`sage_anthropic_key` / `sage_openrouter_key`) so it's one credential concept.
 */
import { isTauri } from "./settings";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export type ChatResult = { ok: true; text: string } | { ok: false; error: string };

const ANTHROPIC_KEY = "sage_anthropic_key";
const OPENROUTER_KEY = "sage_openrouter_key";
const ANTHROPIC_MODEL = "sage_anthropic_model";
const OPENROUTER_MODEL = "sage_openrouter_model";

export const DEFAULT_ANTHROPIC_MODEL = "claude-sonnet-4-6";
export const DEFAULT_OPENROUTER_MODEL = "anthropic/claude-sonnet-4.6";

function ls(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
export function setLocal(key: string, v: string) {
  try { v.trim() ? localStorage.setItem(key, v.trim()) : localStorage.removeItem(key); } catch { /* */ }
}
export { ANTHROPIC_KEY, OPENROUTER_KEY, ANTHROPIC_MODEL, OPENROUTER_MODEL };

/** Which provider is configured + the model badge to show on the pill. */
export function chatStatus(): { provider: "anthropic" | "openrouter" | null; model: string } {
  if (ls(ANTHROPIC_KEY)) return { provider: "anthropic", model: ls(ANTHROPIC_MODEL) || DEFAULT_ANTHROPIC_MODEL };
  if (ls(OPENROUTER_KEY)) return { provider: "openrouter", model: ls(OPENROUTER_MODEL) || DEFAULT_OPENROUTER_MODEL };
  return { provider: null, model: "no key" };
}

/** POST JSON via Rust (desktop) or direct fetch (web dev, CORS-friendly headers). */
async function postJson(url: string, headers: Record<string, string>, body: unknown, timeoutMs = 60_000): Promise<{ status: number; text: string }> {
  const payload = JSON.stringify(body);
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    const out = await invoke<{ status: number; headers: Record<string, string>; body: string }>("mcp_http_post", { url, headers, body: payload, timeoutMs });
    return { status: out.status, text: out.body };
  }
  const res = await fetch(url, { method: "POST", headers, body: payload });
  return { status: res.status, text: await res.text() };
}

export async function chatComplete(messages: ChatMessage[], system: string): Promise<ChatResult> {
  const status = chatStatus();
  if (!status.provider) {
    return { ok: false, error: "no-key" };
  }

  try {
    if (status.provider === "anthropic") {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "x-api-key": ls(ANTHROPIC_KEY) as string,
        "anthropic-version": "2023-06-01",
      };
      if (!isTauri()) headers["anthropic-dangerous-direct-browser-access"] = "true";
      const { status: code, text } = await postJson("https://api.anthropic.com/v1/messages", headers, {
        model: status.model,
        max_tokens: 1500,
        system,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
      });
      if (code !== 200) return { ok: false, error: `Anthropic HTTP ${code}: ${text.slice(0, 160)}` };
      const json = JSON.parse(text);
      const out = (json.content ?? []).filter((c: any) => c.type === "text").map((c: any) => c.text).join("");
      return { ok: true, text: out || "(empty response)" };
    }

    // OpenRouter
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${ls(OPENROUTER_KEY)}`,
      "HTTP-Referer": "https://sage-platform.pages.dev",
      "X-Title": "SAGE Platform",
    };
    const { status: code, text } = await postJson("https://openrouter.ai/api/v1/chat/completions", headers, {
      model: status.model,
      messages: [{ role: "system", content: system }, ...messages],
    });
    if (code !== 200) return { ok: false, error: `OpenRouter HTTP ${code}: ${text.slice(0, 160)}` };
    const json = JSON.parse(text);
    const out = json.choices?.[0]?.message?.content ?? "";
    return { ok: true, text: out || "(empty response)" };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
